import React from 'react';

import PropTypes from 'prop-types';

import { Text, TextGroup } from '@asteria/component-core/typography';

import Chip from '@asteria/component-chip';

import { Translation, useTranslationFn } from '@asteria/language';

import PieGraph from '../../../../../chart/pie';
import { COLORS } from '../../constants';
import { getStatistics, getTotalCount } from '../utils';

/**
 * @typedef { import('../../../basic/types').Props } Props
 * @typedef { import('../../../basic/types').ChildrenProps } ChildrenProps
 */

/** @type { React.FC<{ currency: string, count: number, sums: unknown, translationOptions: ChildrenProps['translationOptions'] }> } */
const Tooltip = React.memo(function Tooltip(props) {
	const { currency, count, sums, translationOptions } = props;

	return (
		<div className="p-2 flex gap-8 items-center justify-between">
			<TextGroup className="flex flex-col gap-1">
				<Chip label={currency} flag={currency} variant="simple" />
				<Translation
					translationKey="card.content.chart.tooltip.content"
					translationOptions={{
						...translationOptions,
						postfix: {
							...translationOptions?.postfix,
							type: 'count',
						},
						data: {
							...translationOptions?.data,
							selected: { currency, count, sums },
						},
					}}
					Component={Text}
					size="xs"
				/>
			</TextGroup>
			<TextGroup>
				<Translation
					translationKey="card.content.chart.tooltip.content"
					translationOptions={{
						...translationOptions,
						postfix: {
							...translationOptions?.postfix,
							type: 'total',
							'type-variant': 'display',
						},
						data: {
							...translationOptions?.data,
							selected: { currency, count, sums },
						},
					}}
					Component={Text}
					weight="medium"
					align="right"
				/>
				{sums?.display?.currency !== sums?.original?.currency ? (
					<Translation
						translationKey="card.content.chart.tooltip.content"
						translationOptions={{
							...translationOptions,
							postfix: {
								...translationOptions?.postfix,
								type: 'total',
								'type-variant': 'original',
							},
							data: {
								...translationOptions?.data,
								selected: { currency, count, sums },
							},
						}}
						Component={Text}
						size="sm"
						align="right"
					/>
				) : null}
			</TextGroup>
		</div>
	);
});

Tooltip.propTypes = {
	currency: PropTypes.string,
	count: PropTypes.number,
	sums: PropTypes.object,
	translationOptions: PropTypes.object,
};

/** @type { React.FC<ChildrenProps & { data: unknown[], type: string, translationData: ReturnType<typeof useTranslationData> }> } */
const Chart = React.memo(function Chart(props) {
	const { translationOptions, data, type, translationData } = props;

	const translate = useTranslationFn();

	const values = React.useMemo(
		() =>
			data.map((value, index) => ({
				id: value.code,
				total: getTotalCount(value),
				absoluteTotal: getTotalCount(value),
				label: value.code,
				fill: 'var(--color)',
				className: `asteria-color__${COLORS[index]}`,
				opacity: !type || type == value.code ? 1 : 0.4,
				tooltip: (
					<Tooltip
						currency={value.code}
						count={getTotalCount(value)}
						sums={{
							original: getStatistics({}, value, 'original'),
							display: getStatistics({}, value, 'display'),
						}}
						translationOptions={translationOptions}
					/>
				),
			})),
		[data, translationOptions, type],
	);

	return (
		<div className="h-52">
			<PieGraph
				data={values}
				dataKey="absoluteTotal"
				innerRadius="65%"
				centerTitle={translate('card.content.chart.title', {
					...translationOptions,
					postfix: {
						...translationOptions?.postfix,
						currency: type,
					},
					data: {
						...translationOptions?.data,
						selected: translationData,
					},
				})}
				centerText={translate('card.content.chart.content', {
					...translationOptions,
					postfix: {
						...translationOptions?.postfix,
						currency: type,
					},
					data: {
						...translationOptions?.data,
						selected: translationData,
					},
				})}
			/>
		</div>
	);
});

Chart.propTypes = {
	query: PropTypes.object,
	data: PropTypes.arrayOf(PropTypes.object),
	type: PropTypes.string,
	translationOptions: PropTypes.object,
	translationData: PropTypes.object,
};

export default Chart;
